import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Box, Typography } from "@mui/material";

const EmptyState = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      minHeight: 160,
      borderRadius: "8px",
      bgcolor: "#f8fafc",
      border: "1px dashed #e2e8f0",
      px: 2,
    }}
  >
    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", textAlign: "center" }}>
      No data available
    </Typography>
  </Box>
);

const CustomTooltip = ({ active, payload, formatValue }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <Box
      sx={{
        bgcolor: "rgba(255,255,255,0.98)",
        border: "1px solid #e2e8f0",
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
        boxShadow: "0 8px 20px rgba(15,23,42,0.12)",
        minWidth: 140,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 0.35, color: "#1a2e2a" }}>
        {row.name}
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
        <Box component="span" sx={{ fontWeight: 700, color: "#334155" }}>
          {formatValue(row.value)}
        </Box>
      </Typography>
    </Box>
  );
};

/**
 * Generic donut-with-legend tile shell shared by Department Revenue,
 * Gender Wise Patients and Appointment Summary. Callers do their own
 * domain-specific data normalization and pass `rows` as
 * [{ name, value, color }] — this component only handles presentation
 * (chart geometry, center overlay, legend column alignment).
 */
const DonutLegendCard = ({
  rows = [],
  formatValue = (v) => String(v),
  centerLabel = "Total",
  valueColHeader = "Value",
  pctColHeader = "Share",
}) => {
  const total = useMemo(
    () => rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0),
    [rows]
  );

  if (!rows.length || total <= 0) {
    return <EmptyState />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 145,
        minWidth: 0,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "stretch",
      }}
    >
      {/* Donut occupies exactly half the tile's width; height follows via aspect-ratio so it fills as much of the tile's height as that half-width allows */}
      <Box
        sx={{
          width: { xs: "100%", sm: "50%" },
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            aspectRatio: "1 / 1",
          }}
        >
          <ResponsiveContainer width="100%" height="100%" debounce={100}>
            <PieChart>
              <Pie
                data={rows}
                cx="50%"
                cy="50%"
                innerRadius="62%"
                outerRadius="96%"
                paddingAngle={rows.length > 1 ? 2 : 0}
                dataKey="value"
                nameKey="name"
                label={false}
                labelLine={false}
                isAnimationActive={false}
              >
                {rows.map((entry, index) => (
                  <Cell key={`cell-${entry.name}-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip formatValue={formatValue} />}
                isAnimationActive={false}
              />
            </PieChart>
          </ResponsiveContainer>

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              px: 1,
            }}
          >
            <Typography sx={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.15 }}>
              {formatValue(total)}
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 700 }}>
              {centerLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Legend occupies the other half; a single shared grid keeps the value/share columns
          aligned across rows (rather than each row sizing independently), while still only
          taking as much width as the longest name needs. */}
      <Box
        sx={{
          width: { xs: "100%", sm: "50%" },
          minWidth: 0,
          display: "grid",
          gridTemplateColumns: "minmax(0, max-content) 44px 40px",
          columnGap: 0.6,
          rowGap: 0.3,
          alignItems: "center",
          alignContent: rows.length > 1 ? "space-evenly" : "center",
          maxWidth: "100%",
        }}
      >
        {rows.map((row, index) => {
          const pct = total > 0 ? ((Number(row.value) || 0) / total) * 100 : 0;
          return (
            <React.Fragment key={`legend-${row.name}-${index}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    bgcolor: row.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.74rem",
                    fontWeight: 600,
                    color: "#334155",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={row.name}
                >
                  {row.name}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.74rem", fontWeight: 800, color: "#0f172a", textAlign: "right" }}>
                {formatValue(row.value)}
              </Typography>
              <Typography sx={{ fontSize: "0.66rem", fontWeight: 600, color: "#94a3b8", textAlign: "right" }}>
                {pct.toFixed(0)}%
              </Typography>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default React.memo(DonutLegendCard);
